import React, { useEffect, useRef, useState } from 'react';
import {
  IoAlertCircleOutline,
  IoBuildOutline,
  IoCameraOutline,
  IoClose,
  IoContractOutline,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoExpandOutline,
  IoGlobeOutline,
  IoOpenOutline,
  IoShieldCheckmarkOutline,
  IoSparklesOutline,
} from 'react-icons/io5';
import useLiveResearch from '../hooks/useLiveResearch';
import { downloadSavedFile, fetchSavedFilePreviewUrl } from '../atlasApi';
import './LiveResearchView.css';

const STATUS_LABELS = {
  loading: 'Loading',
  queued: 'Queued',
  running: 'Running',
  complete: 'Complete',
  error: 'Failed',
  cancelled: 'Cancelled',
};

export function ResearchStatusChip({ status, stalled }) {
  const label = stalled ? 'Stalled' : STATUS_LABELS[status] || status || '—';
  const cls = stalled ? 'error' : status || 'loading';
  return (
    <span className={`research-status-chip research-status-${cls}`}>
      {(status === 'running' || status === 'queued') && !stalled ? (
        <span className="research-status-dot" />
      ) : null}
      {label}
    </span>
  );
}

// Screenshot with async blob loading (BriefImage pattern).
function ResearchScreenshot({ fileId, alt, onClick }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!fileId) return undefined;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        objectUrl = await fetchSavedFilePreviewUrl({ fileId });
        if (!cancelled) setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl && objectUrl.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  if (failed) return <div className="research-screenshot research-screenshot-error">Screenshot unavailable</div>;
  if (!url) return <div className="research-screenshot research-screenshot-loading" />;
  return (
    <button type="button" className="research-screenshot" onClick={onClick} title="Open full size">
      <img src={url} alt={alt || 'Research screenshot'} />
    </button>
  );
}

function traceIcon(kind, ok) {
  if (kind === 'thinking') return <IoSparklesOutline size={13} />;
  if (kind === 'finding') return <IoDocumentTextOutline size={13} />;
  if (kind === 'captcha' || kind === 'safety_check') return <IoShieldCheckmarkOutline size={13} />;
  if (kind === 'error' || ok === false) return <IoAlertCircleOutline size={13} />;
  return <IoBuildOutline size={13} />;
}

function formatDetail(detail) {
  if (!detail || typeof detail !== 'object') return '';
  const parts = [];
  if (detail.x != null && detail.y != null) parts.push(`(${detail.x}, ${detail.y})`);
  if (detail.keys) parts.push(Array.isArray(detail.keys) ? detail.keys.join('+') : String(detail.keys));
  if (detail.text_len != null) parts.push(`${detail.text_len} chars`);
  if (detail.scroll_y) parts.push(`scroll ${detail.scroll_y}`);
  if (detail.url) parts.push(String(detail.url));
  if (detail.code) parts.push(String(detail.code));
  return parts.join(' ');
}

function RunTabStrip({ runTabs, runId, activeName, onSelectRun }) {
  const stripRef = useRef(null);

  // Every run gets a tab — the strip scrolls horizontally — and the active
  // one is always brought into view (works at any panel width and tab count).
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const active = strip.querySelector('.live-research-run-tab.active');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [runId, runTabs.length]);

  const tabLabel = (tab) => ((tab.run_id === runId ? activeName : null) || tab.name || tab.run_id);
  const tabLive = (tab) => tab.status === 'queued' || tab.status === 'running';

  return (
    <div className="live-research-run-tabs" role="tablist" ref={stripRef}>
      {runTabs.map((tab) => {
        const isCurrent = tab.run_id === runId;
        const label = tabLabel(tab);
        return (
          <button
            key={tab.run_id}
            type="button"
            role="tab"
            aria-selected={isCurrent}
            className={`live-research-run-tab${isCurrent ? ' active' : ''}`}
            title={label}
            onClick={() => { if (!isCurrent) onSelectRun(tab.run_id); }}
          >
            {tabLive(tab) ? <span className="live-research-run-tab-live" /> : null}
            <span className="live-research-run-tab-label">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function LiveResearchView({
  runId,
  variant = 'panel',
  onClose,
  isMobile,
  isFullscreen = false,
  onToggleFullscreen,
  runTabs,
  onSelectRun,
  onRunUpdate,
}) {
  const {
    run,
    status,
    connection,
    stepInfo,
    liveUrl,
    latestFrame,
    trace,
    findings,
    captcha,
    result,
    error,
    cancelling,
    cancel,
  } = useLiveResearch({ runId });

  // Screenshot streaming is the default: the Browserless liveURL viewer does
  // not reliably paint when embedded in an iframe (verified live — stays
  // black), but works in its own tab. The iframe stays available as an
  // opt-in, and "open in new tab" is the interactive/CAPTCHA-takeover path.
  const [iframeMode, setIframeMode] = useState(false);
  const [lightboxFileId, setLightboxFileId] = useState(null);
  const traceRef = useRef(null);
  const pinnedRef = useRef(true);

  const isActive = status === 'queued' || status === 'running' || status === 'loading';
  const runName = run?.name;
  useEffect(() => {
    if (!onRunUpdate || !runId || status === 'loading') return;
    onRunUpdate({ run_id: runId, name: runName, status });
  }, [onRunUpdate, runId, runName, status]);

  const runDateLabel = (() => {
    const startedRaw = run?.started_at || run?.created_at;
    if (!startedRaw) return null;
    const started = new Date(startedRaw);
    if (Number.isNaN(started.getTime())) return null;
    const dateOpts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    let label = `Ran ${started.toLocaleString(undefined, dateOpts)}`;
    if (!isActive && run?.finished_at) {
      const finished = new Date(run.finished_at);
      if (!Number.isNaN(finished.getTime())) {
        label += ` · finished ${finished.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
      }
    }
    return label;
  })();
  const showIframe = Boolean(liveUrl) && iframeMode && isActive;

  useEffect(() => {
    const el = traceRef.current;
    if (el && pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [trace]);

  const handleTraceScroll = () => {
    const el = traceRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const name = run?.name || 'Web research';
  const stalled = Boolean(run?.stalled) && isActive;

  return (
    <div className={`live-research live-research-${variant}`}>
      <div className="live-research-header">
        <div className="live-research-title">
          <IoGlobeOutline size={16} className="live-research-title-icon" />
          <span className="live-research-header-label">Live Web Research</span>
          <ResearchStatusChip status={status} stalled={stalled} />
        </div>
        <div className="live-research-header-right">
          {isActive && stepInfo.step ? (
            <span className="live-research-step">
              {stepInfo.maxSteps ? `step ${stepInfo.step}/${stepInfo.maxSteps}` : `step ${stepInfo.step}`}
            </span>
          ) : null}
          {isActive && status !== 'loading' ? (
            <button
              type="button"
              className="live-research-cancel"
              onClick={cancel}
              disabled={cancelling}
            >
              {cancelling ? 'Stopping…' : 'Stop'}
            </button>
          ) : null}
          {variant === 'panel' && onToggleFullscreen ? (
            <button
              type="button"
              className="live-research-close"
              onClick={onToggleFullscreen}
              aria-label={isFullscreen ? 'Show chat' : 'Expand research panel'}
              title={isFullscreen ? 'Show chat' : 'Expand'}
            >
              {isFullscreen ? <IoContractOutline size={17} /> : <IoExpandOutline size={17} />}
            </button>
          ) : null}
          {variant === 'panel' && onClose ? (
            <button type="button" className="live-research-close" onClick={onClose} aria-label="Close research panel">
              <IoClose size={18} />
            </button>
          ) : null}
        </div>
      </div>

      {Array.isArray(runTabs) && runTabs.length > 1 && onSelectRun ? (
        <RunTabStrip runTabs={runTabs} runId={runId} activeName={run?.name} onSelectRun={onSelectRun} />
      ) : null}

      {stepInfo.url ? (
        <div className="live-research-url" title={stepInfo.url}>{stepInfo.url}</div>
      ) : null}

      {status !== 'loading' ? (
        <div className="live-research-intro">
          <div className="live-research-intro-name">{name}</div>
          {runDateLabel ? <div className="live-research-intro-date">{runDateLabel}</div> : null}
          <div className="live-research-intro-note">
            {isActive
              ? 'Atlas will be aware of all the contents here once live web research completes.'
              : 'Atlas is aware of all the content displayed here, so continue in your chat for further analysis.'}
          </div>
        </div>
      ) : null}

      {captcha ? (
        <div className="live-research-captcha">
          <IoShieldCheckmarkOutline size={15} />
          <span>{captcha.message}</span>
        </div>
      ) : null}

      {/* The recording area only exists while the run is live; finished runs
          lead with their summary/findings instead. */}
      {isActive ? (
        <div className="live-research-viewport">
          {showIframe ? (
            <>
              <iframe
                src={liveUrl}
                title="Live browser session"
                className="live-research-iframe"
                sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-popups"
                allow="clipboard-read; clipboard-write"
                referrerPolicy="no-referrer"
              />
              <button
                type="button"
                className="live-research-view-toggle"
                onClick={() => setIframeMode(false)}
                title="Back to streamed screenshots"
              >
                <IoCameraOutline size={13} /> Screenshots
              </button>
            </>
          ) : latestFrame || isActive ? (
            <>
              {latestFrame ? (
                <img className="live-research-frame" src={latestFrame.src} alt="Latest browser screenshot" />
              ) : (
                <div className="live-research-viewport-empty">Waiting for the first screenshot…</div>
              )}
              {liveUrl && isActive ? (
                <div className="live-research-viewport-actions">
                  <button
                    type="button"
                    className="live-research-view-toggle"
                    onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}
                    title="Open the interactive live session in a new tab (lets you take over, e.g. for a stubborn verification)"
                  >
                    <IoOpenOutline size={13} /> Open live
                  </button>
                  <button
                    type="button"
                    className="live-research-view-toggle"
                    onClick={() => setIframeMode(true)}
                    title="Try the embedded live view"
                  >
                    <IoGlobeOutline size={13} /> Embed
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="live-research-viewport-empty">
              {connection === 'connecting' || connection === 'reconnecting' || status === 'loading'
                ? 'Connecting to the browser session…'
                : status === 'queued'
                  ? 'Waiting for the browser to start…'
                  : 'Waiting for the first screenshot…'}
            </div>
          )}
        </div>
      ) : null}

      {isActive || trace.length > 0 ? (
      <div className="live-research-trace" ref={traceRef} onScroll={handleTraceScroll}>
        {trace.length === 0 && isActive ? (
          <div className="live-research-trace-empty">The agent's thinking and actions will appear here.</div>
        ) : null}
        {trace.map((entry) => (
          <div key={entry.id} className={`live-research-trace-entry trace-${entry.kind}${entry.ok === false ? ' trace-failed' : ''}`}>
            <span className="trace-icon">{traceIcon(entry.kind, entry.ok)}</span>
            <span className="trace-body">
              <span className="trace-text">{entry.text}</span>
              {entry.detail ? <span className="trace-detail">{formatDetail(entry.detail)}</span> : null}
              {entry.message && entry.kind !== 'thinking' ? (
                <span className="trace-message">{entry.message}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
      ) : null}

      {!isActive ? (
        <div className={`live-research-terminal${trace.length === 0 ? ' live-research-terminal-full' : ''}`}>
          {status === 'complete' && result ? (
            <>
              {result.summary ? (
                <div className="live-research-summary">
                  <div className="live-research-section-title">Summary</div>
                  <div className="live-research-summary-text">{result.summary}</div>
                </div>
              ) : null}
              {(result.findings || findings || []).length > 0 ? (
                <div className="live-research-findings">
                  <div className="live-research-section-title">
                    Findings ({(result.findings || findings).length})
                  </div>
                  {(result.findings || findings).map((finding, index) => (
                    <div className="live-research-finding" key={finding.index || index}>
                      <div className="live-research-finding-text">
                        <div className="live-research-finding-label">{finding.label}</div>
                        <div className="live-research-finding-note">{finding.note}</div>
                        {finding.url ? (
                          <a className="live-research-finding-url" href={finding.url} target="_blank" rel="noreferrer">
                            {finding.url}
                          </a>
                        ) : null}
                      </div>
                      {finding.screenshot_file_id || finding.screenshotFileId ? (
                        <div className="live-research-finding-shot">
                          <ResearchScreenshot
                            fileId={finding.screenshot_file_id || finding.screenshotFileId}
                            alt={finding.label}
                            onClick={() => setLightboxFileId(finding.screenshot_file_id || finding.screenshotFileId)}
                          />
                          <button
                            type="button"
                            className="live-research-download"
                            title="Download screenshot"
                            onClick={() =>
                              downloadSavedFile({
                                fileId: finding.screenshot_file_id || finding.screenshotFileId,
                                filename: `${finding.label || 'finding'}.png`,
                              }).catch(() => {})
                            }
                          >
                            <IoDownloadOutline size={14} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {(result.sources || []).length > 0 ? (
                <div className="live-research-sources">
                  <div className="live-research-section-title">Sources</div>
                  {result.sources.map((source, index) => (
                    <div className="live-research-source" key={index}>
                      <a href={source.url} target="_blank" rel="noreferrer">{source.title || source.url}</a>
                      {source.note ? <span className="live-research-source-note"> — {source.note}</span> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : status === 'cancelled' ? (
            <div className="live-research-error-box">This run was stopped.</div>
          ) : error ? (
            <div className="live-research-error-box">
              <IoAlertCircleOutline size={15} /> {error}
            </div>
          ) : null}
        </div>
      ) : null}

      {lightboxFileId ? (
        <div className="live-research-lightbox" onClick={() => setLightboxFileId(null)}>
          <ResearchScreenshot fileId={lightboxFileId} alt="Screenshot" onClick={() => setLightboxFileId(null)} />
        </div>
      ) : null}
    </div>
  );
}
